import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["stopsOtherMediaWhenPlaying", "resetOnEnded"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// Mostly copied from react-use

/* eslint-disable no-console */
import * as React from 'react';
export default function createHTMLMediaHook(tag) {
  return function (elOrProps) {
    var element = /*#__PURE__*/React.isValidElement(elOrProps) ? elOrProps : undefined;
    var propsMapped = /*#__PURE__*/React.isValidElement(elOrProps) ? elOrProps.props : elOrProps;
    var _propsMapped$stopsOth = propsMapped.stopsOtherMediaWhenPlaying,
      stopsOtherMediaWhenPlaying = _propsMapped$stopsOth === void 0 ? true : _propsMapped$stopsOth,
      _propsMapped$resetOnE = propsMapped.resetOnEnded,
      resetOnEnded = _propsMapped$resetOnE === void 0 ? true : _propsMapped$resetOnE,
      props = _objectWithoutProperties(propsMapped, _excluded);
    var _useSetState = useSetState({
        canPlay: false,
        canPlayThrough: false,
        buffered: [],
        currentTime: 0,
        duration: 0,
        paused: true,
        muted: false,
        volume: 1,
        playbackRate: 1,
        loadedMetadata: false
      }),
      _useSetState2 = _slicedToArray(_useSetState, 2),
      state = _useSetState2[0],
      setState = _useSetState2[1];
    var ref = React.useRef(null);
    var wrapEvent = function wrapEvent(userEvent, proxyEvent) {
      return function (event) {
        try {
          proxyEvent === null || proxyEvent === void 0 || proxyEvent(event);
        } finally {
          userEvent === null || userEvent === void 0 || userEvent(event);
        }
      };
    };
    var onPlay = function onPlay() {
      return setState({
        paused: false
      });
    };
    var onPause = function onPause() {
      return setState({
        paused: true
      });
    };
    var onCanPlay = function onCanPlay() {
      return setState({
        canPlay: true
      });
    };
    var onCanPlayThrough = function onCanPlayThrough() {
      return setState({
        canPlayThrough: true
      });
    };
    var onLoadedMetadata = function onLoadedMetadata() {
      return setState({
        loadedMetadata: true
      });
    };
    var onVolumeChange = function onVolumeChange() {
      var el = ref.current;
      if (!el) {
        return;
      }
      setState({
        muted: el.muted,
        volume: el.volume
      });
    };
    var onDurationChange = function onDurationChange() {
      var el = ref.current;
      if (!el) {
        return;
      }
      var duration = el.duration,
        buffered = el.buffered;
      setState({
        duration: duration,
        buffered: parseTimeRanges(buffered)
      });
    };
    var onTimeUpdate = function onTimeUpdate() {
      var el = ref.current;
      if (!el) {
        return;
      }
      setState({
        currentTime: el.currentTime
      });
    };
    var onProgress = function onProgress() {
      var el = ref.current;
      if (!el) {
        return;
      }
      setState({
        buffered: parseTimeRanges(el.buffered)
      });
    };
    var onRateChange = function onRateChange() {
      var el = ref.current;
      if (!el) {
        return;
      }
      setState({
        playbackRate: el.playbackRate
      });
    };
    var onEnded = function onEnded() {
      if (!resetOnEnded) return;
      var el = ref.current;
      if (!el) {
        return;
      }
      el.currentTime = 0;
    };
    var elementProps = _objectSpread(_objectSpread({
      controls: false
    }, props), {}, {
      ref: ref,
      onPlay: wrapEvent(props.onPlay, onPlay),
      onPause: wrapEvent(props.onPause, onPause),
      onCanPlay: wrapEvent(props.onCanPlay, onCanPlay),
      onCanPlayThrough: wrapEvent(props.onCanPlayThrough, onCanPlayThrough),
      onLoadedMetadata: wrapEvent(props.onLoadedMetadata, onLoadedMetadata),
      onVolumeChange: wrapEvent(props.onVolumeChange, onVolumeChange),
      onDurationChange: wrapEvent(props.onDurationChange, onDurationChange),
      onTimeUpdate: wrapEvent(props.onTimeUpdate, onTimeUpdate),
      onProgress: wrapEvent(props.onProgress, onProgress),
      onRateChange: wrapEvent(props.onRateChange, onRateChange),
      onEnded: wrapEvent(props.onEnded, onEnded)
    });
    element = element ? /*#__PURE__*/React.cloneElement(element, elementProps) : /*#__PURE__*/React.createElement(tag, elementProps);

    // Some browsers return `Promise` on `.play()` and may throw errors
    // if one tries to execute another `.play()` or `.pause()` while that
    // promise is resolving. So we prevent that with this lock.
    // See: https://bugs.chromium.org/p/chromium/issues/detail?id=593273
    var lockPlay = false;
    var controls = {
      play: function play() {
        if (stopsOtherMediaWhenPlaying) {
          var _document;
          var media = (_document = document) === null || _document === void 0 ? void 0 : _document.querySelectorAll('audio,video');
          for (var i = 0; i < media.length; i += 1) {
            media[i].pause();
          }
        }
        var el = ref.current;
        if (!el) {
          return undefined;
        }
        if (!lockPlay) {
          var promise = el.play();
          var isPromise = typeof promise === 'object';
          if (isPromise) {
            lockPlay = true;
            var resetLock = function resetLock() {
              lockPlay = false;
            };
            promise.then(resetLock, resetLock);
          }
          return promise;
        }
        return undefined;
      },
      pause: function pause() {
        var el = ref.current;
        if (el && !lockPlay) {
          el.pause();
        }
      },
      seek: function seek(currentTime) {
        var el = ref.current;
        if (!el || state.duration === undefined) {
          return;
        }
        var fixedTime = Math.min(state.duration, Math.max(0, currentTime));
        el.currentTime = fixedTime;
        setState({
          currentTime: fixedTime
        });
      },
      setVolume: function setVolume(volume) {
        var el = ref.current;
        if (!el) {
          return;
        }
        var fixedVolume = Math.min(1, Math.max(0, volume));
        el.volume = fixedVolume;
        setState({
          volume: fixedVolume
        });
      },
      mute: function mute() {
        var el = ref.current;
        if (!el) {
          return;
        }
        el.muted = true;
      },
      unmute: function unmute() {
        var el = ref.current;
        if (!el) {
          return;
        }
        el.muted = false;
      },
      setPlaybackRate: function setPlaybackRate(rate) {
        var el = ref.current;
        if (!el || state.duration === undefined) {
          return;
        }
        el.playbackRate = rate;
        setState({
          playbackRate: rate
        });
      }
    };
    React.useEffect(function () {
      var el = ref.current;
      if (!el) {
        if (process.env.NODE_ENV !== 'production') {
          if (tag === 'audio') {
            console.error('useAudio() ref to <audio> element is empty at mount. ' + 'It seem you have not rendered the audio element, which it ' + 'returns as the first argument const [audio] = useAudio(...).');
          } else if (tag === 'video') {
            console.error('useVideo() ref to <video> element is empty at mount. ' + 'It seem you have not rendered the video element, which it ' + 'returns as the first argument const [video] = useVideo(...).');
          }
        }
        return;
      }
      setState({
        volume: el.volume,
        muted: el.muted,
        paused: el.paused
      });

      // Start media, if autoPlay requested.
      if (props.autoPlay && el.paused) {
        controls.play();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.src]);
    return [element, state, controls, ref];
  };
}
function parseTimeRanges(ranges) {
  var result = [];
  for (var i = 0; i < ranges.length; i++) {
    result.push({
      start: ranges.start(i),
      end: ranges.end(i)
    });
  }
  return result;
}
function useSetState() {
  var initialState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _React$useState = React.useState(initialState),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    state = _React$useState2[0],
    set = _React$useState2[1];
  var setState = React.useCallback(function (patch) {
    set(function (prevState) {
      return Object.assign({}, prevState, patch instanceof Function ? patch(prevState) : patch);
    });
  }, []);
  return [state, setState];
}